import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, AlertCircle, Check, Clock, Globe, Loader2, RefreshCw,
  ShieldAlert, ShieldCheck, ExternalLink,
} from "lucide-react";

const fmtAgo = (iso) => {
  if (!iso) return "—";
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 1440)}d ago`;
};

export default function BrandHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/health-monitor/my");
      setData(data);
    } finally { setLoading(false); }
  };

  const probe = async () => {
    setProbing(true);
    try {
      const { data } = await api.post("/health-monitor/probe");
      toast.success(`Probe complete · ${data.ok ? "healthy" : "issues detected"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Probe failed");
    } finally { setProbing(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="h-64 glass rounded-2xl animate-pulse" />;

  if (!data?.has_custom_domain) {
    return (
      <div className="glass rounded-2xl p-8" data-testid="health-no-domain">
        <div className="text-center text-white/60 text-sm">
          Brand Health monitoring activates once you set a <a href="/dashboard/whitelabel" className="text-indigo-300 underline">white-label custom domain</a>. We'll probe the domain hourly for HTTPS, cert expiry, and tenant routing.
        </div>
      </div>
    );
  }

  if (!data.domain_verified) {
    return (
      <div className="glass rounded-2xl p-8" data-testid="health-not-verified">
        <div className="text-center text-white/60 text-sm">
          Verify the DNS TXT record at <a href="/dashboard/whitelabel" className="text-indigo-300 underline">/dashboard/whitelabel</a> to unlock Brand Health monitoring.
        </div>
      </div>
    );
  }

  const s = data.snapshot;
  const ok = s?.ok === true;
  const certDays = s?.cert?.days_remaining;

  return (
    <div className="space-y-6" data-testid="brand-health-root">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" /> Brand Health · Managed-SaaS
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">
            {s?.brand_name || "Your brand"} <span className="text-white/40">·</span> {s?.domain || "—"}
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            Hourly probes of <code className="text-indigo-300">/api/health</code> + <code className="text-indigo-300">/share/test</code>, SSL cert expiry, and tenant routing. Last check: <span className="text-white/70">{fmtAgo(s?.checked_at)}</span>.
          </p>
        </div>
        <button onClick={probe} disabled={probing} data-testid="health-probe-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {probing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Probe now
        </button>
      </header>

      <section className="grid sm:grid-cols-3 gap-3">
        <StatusTile
          label="Overall"
          state={s == null ? "unknown" : ok ? "ok" : "fail"}
          detail={s == null ? "No probes yet" : ok ? "All checks passing" : "1 or more checks failed"}
          testid="health-tile-overall"
        />
        <StatusTile
          label="HTTPS routing"
          state={(s?.probes || []).every((p) => p.ok) ? "ok" : "fail"}
          detail={`${(s?.probes || []).filter((p) => p.ok).length}/${(s?.probes || []).length || 0} probes OK`}
          testid="health-tile-https"
        />
        <StatusTile
          label="SSL certificate"
          state={
            !s?.cert ? "unknown" :
            s.cert.error ? "fail" :
            certDays != null && certDays <= 14 ? "warn" :
            "ok"
          }
          detail={
            !s?.cert ? "—" :
            s.cert.error ? s.cert.error :
            `${certDays}d remaining · ${s.cert.issuer || "DV"}`
          }
          testid="health-tile-ssl"
        />
      </section>

      <section className="glass rounded-2xl p-6" data-testid="health-probes">
        <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
          <Globe className="h-4 w-4 text-indigo-400" /> Latest probes
        </h3>
        {(s?.probes || []).length === 0 ? (
          <div className="text-sm text-white/40 text-center py-6">No probes yet — click "Probe now" or wait up to 1 hour for the next scheduled run.</div>
        ) : (
          <div className="space-y-2">
            {s.probes.map((p) => (
              <div key={p.url} className="glass rounded-lg p-3 flex items-center gap-3 text-xs" data-testid={`probe-${p.url.replace(/[^a-z0-9]/gi, '-')}`}>
                {p.ok ? <Check className="h-4 w-4 text-emerald-400 shrink-0" /> : <AlertCircle className="h-4 w-4 text-rose-400 shrink-0" />}
                <code className="text-indigo-300 flex-1 truncate">{p.url}</code>
                <span className="text-white/60 font-mono w-16 text-right">{p.status ?? "—"}</span>
                <span className="text-white/40 font-mono w-20 text-right">{p.latency_ms ? `${p.latency_ms}ms` : "—"}</span>
                <a href={p.url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white">
                  <ExternalLink className="h-3 w-3" />
                </a>
                {p.error && <span className="text-rose-300 ml-2 truncate max-w-[40%]">{p.error}</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="glass rounded-2xl p-6" data-testid="health-events">
        <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-indigo-400" /> Recent events
        </h3>
        {(data.events || []).length === 0 ? (
          <div className="text-sm text-white/40 text-center py-6">No events recorded yet.</div>
        ) : (
          <div className="space-y-1">
            {data.events.map((e, i) => (
              <div key={i} className="flex items-center gap-3 text-xs glass rounded-lg p-2">
                <span className="font-mono text-white/40 w-36 shrink-0">{new Date(e.checked_at).toLocaleString()}</span>
                {e.ok ? <Check className="h-3 w-3 text-emerald-400" /> : <AlertCircle className="h-3 w-3 text-rose-400" />}
                {e.cert_warning && <ShieldAlert className="h-3 w-3 text-amber-400" />}
                <span className="text-white/60 flex-1 truncate">
                  {e.ok ? "All checks healthy" : e.first_error || "Check failed"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusTile({ label, state, detail, testid }) {
  const palette = {
    ok:      { bg: "bg-emerald-500/15", text: "text-emerald-300", icon: ShieldCheck },
    warn:    { bg: "bg-amber-500/15",   text: "text-amber-300",   icon: ShieldAlert },
    fail:    { bg: "bg-rose-500/15",    text: "text-rose-300",    icon: AlertCircle },
    unknown: { bg: "bg-white/5",        text: "text-white/60",    icon: Activity   },
  }[state];
  const Icon = palette.icon;
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</div>
        <div className={`h-7 w-7 rounded-full ${palette.bg} grid place-items-center`}>
          <Icon className={`h-3.5 w-3.5 ${palette.text}`} />
        </div>
      </div>
      <div className={`font-heading text-xl font-black ${palette.text}`}>{state.toUpperCase()}</div>
      <div className="text-xs text-white/60 mt-1">{detail}</div>
    </div>
  );
}
